import React from 'react';

function SimInsertionGuide() {
  return (
    <div className="container py-4" style={{ maxWidth: '800px', fontSize: '14px' }}>
      <h4 className="mb-4">Ejoin Gateway – SIM Insertion Guide</h4>

      {/* Section 1 */}
      <div className="mb-4">
        <h5>1. Before You Start</h5>
        <ul>
          <li>Check the SIM is full size to match the SIM in the images below. Full size SIM pictured in step 3.</li>
          <li>Check the gold part of the SIM is clean and has no scratches.</li>
        </ul>
      </div>

      {/* Section 2 */}
      <div className="mb-4">
        <h5>2. Understanding the Layout</h5>
        <p>
          The SIMs are laid out in the pattern pictured. Each numbered row can fit 16 SIM cards (slots 1–16)
          and only one of each row can be active at one time.
        </p>
        <p>
          Each row is numbered starting from the bottom. Within a row, <strong>1.01</strong> is in the bottom left,{' '}
          <strong>1.02</strong> is the slot directly above 1.01, and <strong>1.03</strong> is the slot to the right of
          1.01. This pattern continues to the right of the box, ending at <strong>1.16</strong>.
        </p>
        <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '13px' }}>
          <strong>Port Assignment Order:</strong> When adding new SIMs, ports are assigned in this sequence:
          <br />• <strong>Slot 1:</strong> 1.01 → 2.01 → 3.01 → … → 16.01
          <br />• <strong>Slot 2:</strong> 1.02 → 2.02 → 3.02 → … → 16.02
          <br />• Continue through all 16 slots, filling each slot across all rows 1–16 before moving to the next.
        </div>
        <img
          src="/images/labelledBox.jpg"
          alt="SIM panel showing labeled port slots"
          className="rounded border mb-1"
          style={{ maxWidth: '500px', width: '100%', height: 'auto', display: 'block' }}
        />
        <p className="text-muted" style={{ fontSize: '12px' }}>
          <strong>Photo:</strong> SIM panel layout showing the row and slot numbering pattern.
        </p>
      </div>

      {/* Section 3 */}
      <div className="mb-4">
        <h5>3. SIM Orientation and Insertion</h5>
        <p>
          The gold contacts for a single row must always face each other as shown in the photos below.
        </p>
        <ol>
          <li>Align the SIM with the opening of the target slot in the orientation described above.</li>
          <li>Gently slide the SIM straight into the slot along the guides until it stops.</li>
          <li>The coloured top edge of the SIM should remain visible, level with the other filled slots.</li>
          <li>Check that the SIM sits flat and straight and does not wobble loosely.</li>
        </ol>
        <img
          src="/images/insertingInto1A.jpg"
          alt="SIM card held in correct orientation before insertion"
          className="rounded border mb-1"
          style={{ maxWidth: '500px', width: '100%', height: 'auto', display: 'block' }}
        />
        <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
          <strong>Photo:</strong> SIM held in correct orientation — gold contacts facing each other.
        </p>
        <img
          src="/images/InsertingInto1B.jpg"
          alt="SIM card fully inserted and seated"
          className="rounded border mb-1"
          style={{ maxWidth: '500px', width: '100%', height: 'auto', display: 'block' }}
        />
        <p className="text-muted" style={{ fontSize: '12px' }}>
          <strong>Photo:</strong> SIM fully inserted and seated, aligned with neighbouring SIMs.
        </p>
      </div>

      {/* Section 4 */}
      <div className="mb-4">
        <h5>4. Removing a SIM</h5>
        <ol>
          <li>Grip the visible coloured portion of the SIM.</li>
          <li>Pull the SIM straight out of the slot without twisting.</li>
        </ol>
      </div>

      {/* Section 5 */}
      <div className="mb-4">
        <h5>5. Registering the SIM in the System</h5>
        <p>
          Once the SIM is physically inserted it must be registered in the portal before it becomes active.
          Follow the steps below carefully.
        </p>
        <ol style={{ lineHeight: '1.9' }}>
          <li>
            Navigate to the <strong>Ports</strong> page in the staff menu.
          </li>
          <li>
            Select the <strong>gateway</strong> that corresponds to the SIM box you have just inserted into.
          </li>
          <li>
            Enter the port number in <strong>letter format</strong> (e.g. <strong>1A</strong>, <strong>3C</strong>) —
            office SIM boxes use this format rather than the dot notation used elsewhere.
          </li>
          <li>
            Enter the <strong>phone number</strong> printed on or associated with the SIM card.
          </li>
          <li>
            Enter the <strong>user code</strong> for the person this number belongs to. The Slack ID field
            should populate automatically. If this is a new user whose Slack ID has not been saved before,
            enter it manually once — it will be remembered for all future entries.
          </li>
        </ol>

        <div className="alert alert-info py-2 mb-3" style={{ fontSize: '13px' }}>
          <strong>Unassigned SIMs:</strong> If the SIM is not being assigned to any particular user, leave the
          email and user code fields blank.
        </div>

        <ol start={6} style={{ lineHeight: '1.9' }}>
          <li>
            If the SIM <strong>is</strong> being assigned to someone, enter their <strong>email address</strong> and{' '}
            <strong>user code</strong>. If that email does not already exist in the{' '}
            <strong>Advice Passwords</strong> section it must be added there as well — without it the number
            will not be visible to the user in their dashboard.
          </li>
          <li>
            Once the port is saved, <strong>send a test message</strong> to confirm the SIM is connected and
            responding correctly.
          </li>
        </ol>
      </div>

      {/* Section 6 */}
      <div className="mb-4">
        <h5>6. Ongoing Checks</h5>
        <p>
          Periodically review the Ports page and check that every number has a recent{' '}
          <strong>last message sent</strong> date. Any number without a recent date may indicate a SIM has
          become disconnected or has been removed from the box.
        </p>
      </div>

      {/* Section 7 */}
      <div className="mb-2">
        <h5>7. Send SMS Protocol — Speak to Sunny</h5>
        <div className="alert alert-warning py-2" style={{ fontSize: '13px' }}>
          <strong>Important:</strong> Whenever a SIM is added to a box for the first time, the{' '}
          <strong>Send SMS protocol</strong> must be triggered. Speak to <strong>Sunny</strong> to arrange
          this. It should be done <strong>no later than one week</strong> after the SIM is inserted.
          <br /><br />
          This process works best when done in batches — if multiple new SIMs are being added around the same
          time, coordinate them together to save time rather than triggering the protocol individually for
          each one.
        </div>
      </div>
    </div>
  );
}

export default SimInsertionGuide;
